/**
 * =============================================================================
 * BACKEND ADAPTER SELECTION
 * =============================================================================
 *
 * The only module the UI imports. Booking.jsx calls `submitBooking(...)` and has
 * no idea which implementation answers — that is the entire point of this layer.
 *
 *     import { submitBooking } from "@/lib/backend";
 *
 * Configuration lives in ./config.js. Read that file to change backends.
 *
 * -----------------------------------------------------------------------------
 * THE NORMALISE-DOWN DECISION — read this before "improving" either adapter
 * -----------------------------------------------------------------------------
 * The two backends do not have equal capabilities:
 *
 *   FastAPI      returns a readable JSON body with the created booking's id.
 *   Apps Script  cannot. Its Web App answers with a 302 to
 *                script.googleusercontent.com, and that response carries no CORS
 *                headers, so the browser will not let JavaScript read it. The
 *                request either completes opaquely or fails outright.
 *
 * We deliberately NORMALISE DOWN to the weaker of the two. Both adapters resolve
 * optimistically on a completed request, and the notification email — not the
 * HTTP response — is the real confirmation.
 *
 * Why give up FastAPI's better guarantee:
 *
 *   The alternative is a UI that shows a confirmed booking with a reference
 *   number on one backend and an optimistic "we think that worked" on the other.
 *   The component would have to know which one it was talking to in order to
 *   render correctly — and at that point the abstraction has leaked into exactly
 *   the place it exists to protect. Every future adapter would have to declare
 *   its capability tier, and every consumer would have to branch on it.
 *
 *   Normalising down costs one piece of information the UI never displayed
 *   anyway. It buys a component that genuinely does not care what is behind it.
 *
 * The trade-off is real and worth naming plainly: **on either adapter, a visitor
 * can be shown the success panel for a booking that did not reach anyone.** The
 * mitigation is not a better HTTP response — it is the notification email, plus
 * the "send as text" accelerator and the phone number on the panel itself.
 *
 * If a future backend can prove delivery synchronously and you want the UI to
 * reflect that, the honest change is to raise the contract for everyone, not to
 * special-case one adapter.
 */

import { ACTIVE_ADAPTER, ADAPTERS } from "./config";
import { submitBooking as submitViaFastApi } from "./fastApi";
import { submitBooking as submitViaAppsScript } from "./appsScript";

/**
 * The selected implementation. Both adapters export an identical signature:
 *
 *   (data: BookingSubmission) => Promise<BookingResult>
 *
 * Resolved once at module load. The adapter cannot change at runtime, which is
 * correct — the config it reads is inlined at build time anyway.
 */
export const submitBooking =
  ACTIVE_ADAPTER === ADAPTERS.APPS_SCRIPT ? submitViaAppsScript : submitViaFastApi;

/** Which adapter is live. Exported for diagnostics; the UI must not branch on it. */
export const activeAdapter = ACTIVE_ADAPTER;

// Re-exported so consumers have one import path for the whole layer.
export { ADAPTERS } from "./config";
export {
  ERRORS,
  REQUEST_TIMEOUT_MS,
  REQUIRED_FIELDS,
  validateBooking,
  isPlausibleEmail,
  buildPayload,
} from "./contract";
